import React, { useCallback, useEffect, useRef, useState } from "react";

import { focusElement } from "../utils/keyboardFocus";

export const Form = (props: {
    id?: string;
    name?: string;
    className?: string;
    onSubmit?: (event: React.FormEvent<HTMLFormElement>) => Promise<void>;
    noValidate?: boolean;
    children?: React.ReactNode;
    ref?: React.RefObject<HTMLFormElement | null>;
}): React.ReactNode => {
    const { className = "form", children, onSubmit: onSubmitProp } = props;
    const internalRef = useRef<HTMLFormElement>(null);
    const ref = props.ref || internalRef;
    const [submitLock, setSubmitLock] = useState(false);
    const [shouldResetInputFocus, setShouldResetInputFocus] = useState(false);

    // Required: Prevents infinite loop in useEffect dependency array
    const setChildInputFocus = useCallback((): void => {
        if (!ref.current) {
            return;
        }
        // Look to see if any inputs in the form have errors. If they do, set
        // keyboard focus to the first input with an error.
        const errorFields = ref.current.querySelectorAll<HTMLElement>(
            ".form__field.form__field--has-errors",
        );
        if (errorFields.length > 0) {
            const fieldInput = errorFields[0].querySelector<HTMLElement>(
                "input, select, textarea",
            );
            // focus on first input field with errors
            if (fieldInput) {
                focusElement(fieldInput);
                // if error no input elems in errored fields, focus on first input field in form
            } else {
                const firstInputField = ref.current.querySelector<HTMLElement>(
                    "input, select, textarea",
                );
                if (firstInputField) {
                    focusElement(firstInputField);
                }
            }
        }
    }, [ref]);

    // Handle focus reset after form submission
    useEffect(() => {
        if (shouldResetInputFocus) {
            setChildInputFocus();
            setShouldResetInputFocus(false);
        }
    }, [shouldResetInputFocus, setChildInputFocus]);

    const onSubmit = async (evnt: React.FormEvent<HTMLFormElement>) => {
        evnt.preventDefault();
        if (submitLock) {
            console.log(
                "Ignoring form submit event because submitLock is active.",
            );
            return;
        }

        // Set latch to true so that later on, when useEffect is called (due to
        // error messages being added to the form), we can set the appropriate input
        // focus. The latch is needed to prevent changing focus after typing every character.
        setShouldResetInputFocus(true);

        if (onSubmitProp) {
            // Prevent multiple form submission
            setSubmitLock(true);
            try {
                await onSubmitProp(evnt);
            } catch (err) {
                console.error(err);
            }
            // Unlock form submission
            setSubmitLock(false);
            setChildInputFocus();
        }
    };

    return (
        <form
            id={props.id}
            name={props.name}
            className={className}
            noValidate={props.noValidate}
            ref={ref}
            onSubmit={onSubmit}
        >
            {children}
        </form>
    );
};
